import React from 'react';
import { LegalLayout, H2, P, UL, Note } from './LegalLayout';
import { COMPANY } from './company';

const Privacy: React.FC = () => (
  <LegalLayout title="Privacy Policy" lastUpdated="7 September 2026">
    <P>
      This Privacy Policy explains how {COMPANY.legalEntity} ("we", "us", "our") collects, uses
      and protects your personal information when you use {COMPANY.appName} (the "Service"). We
      process personal information in line with South Africa's Protection of Personal Information
      Act, 2013 (POPIA). By using the Service you acknowledge this Policy.
    </P>

    <H2>1. Who is responsible for your information</H2>
    <P>
      The responsible party is {COMPANY.legalEntity}
      {COMPANY.registrationNumber ? ` (registration number ${COMPANY.registrationNumber})` : ''}.
      You can reach us about privacy at {COMPANY.privacyEmail}
      {COMPANY.address ? `, or at ${COMPANY.address}, ${COMPANY.country}` : ''}.
    </P>

    <H2>2. Information we collect</H2>
    <UL>
      <li><strong>Account details</strong> — name, email address, and (optionally) phone number, Handicap Index and home club.</li>
      <li><strong>Profile content</strong> — a profile photo and any social links you choose to add.</li>
      <li><strong>Sign-in data</strong> — if you use Google Sign-In, we receive your name and verified email from Google. We never receive your Google password.</li>
      <li><strong>Wallet and transaction data</strong> — your wallet balance, deposits, withdrawals, fees, bets and settlements. Card payments are handled by our payment provider; we do not store your card numbers.</li>
      <li><strong>Game data</strong> — games, scores, bets and messages you create or take part in.</li>
      <li><strong>Technical data</strong> — basic device/log information needed to operate and secure the Service.</li>
    </UL>

    <H2>3. How we use your information</H2>
    <UL>
      <li>to create and manage your account and provide the Service;</li>
      <li>to process deposits, withdrawals, wagers and settlements;</li>
      <li>to verify identity/age and prevent fraud, cheating and money laundering;</li>
      <li>to send service messages such as password resets and email verification;</li>
      <li>to provide support and respond to your requests; and</li>
      <li>to comply with legal obligations and enforce our Terms.</li>
    </UL>

    <H2>4. Legal basis for processing</H2>
    <P>
      We process your information to perform our contract with you (providing the Service), to
      comply with legal obligations (such as anti-fraud and record-keeping), for our legitimate
      interests in running and securing the platform, and with your consent where required.
    </P>

    <H2>5. Who we share it with</H2>
    <P>We share personal information only as needed to run the Service, including with:</P>
    <UL>
      <li><strong>PayFast</strong> — our payment gateway, to process deposits and withdrawals.</li>
      <li><strong>Supabase</strong> — our database and file-storage provider, which hosts your account data and uploaded images.</li>
      <li><strong>Google</strong> — if you choose Google Sign-In, to authenticate you.</li>
      <li><strong>Google Analytics</strong> — only after you allow analytics cookies, to help us understand page visits and product usage.</li>
      <li><strong>Email provider</strong> — to deliver transactional emails (e.g. password reset and verification).</li>
      <li><strong>Other players</strong> — your name, profile photo and game/bet activity are visible to players in the games you join.</li>
      <li><strong>Authorities</strong> — where required by law or to protect our rights or users.</li>
    </UL>
    <P>We do not sell your personal information.</P>

    <H2>6. Storage and international transfers</H2>
    <P>
      Some of our service providers may process data outside South Africa. Where this happens, we
      take reasonable steps to ensure your information receives a comparable level of protection as
      required by POPIA.
    </P>

    <H2>7. How long we keep it</H2>
    <P>
      We keep your information for as long as your account is active and thereafter only as long as
      necessary to meet legal, accounting, tax and anti-fraud obligations, after which it is
      deleted or anonymised.
    </P>

    <H2>8. How we protect it</H2>
    <P>
      We use appropriate technical and organisational measures to protect your information,
      including encryption of sensitive wallet data, hashed passwords, encrypted connections
      (HTTPS) and access controls. No system is perfectly secure, so we cannot guarantee absolute
      security.
    </P>

    <H2>9. Your rights</H2>
    <P>Under POPIA you have the right to:</P>
    <UL>
      <li>access the personal information we hold about you;</li>
      <li>ask us to correct or delete it;</li>
      <li>object to certain processing;</li>
      <li>withdraw consent where processing relies on it; and</li>
      <li>lodge a complaint with the Information Regulator (South Africa).</li>
    </UL>
    <P>To exercise any of these rights, email {COMPANY.privacyEmail}.</P>

    <H2>10. Cookies and local storage</H2>
    <P>
      We use your browser's local storage to keep you signed in (for example, your session tokens)
      and to remember basic preferences. These are essential to how the Service works. If you
      choose “Allow analytics”, Google Analytics sets analytics cookies and receives information
      about page visits and product interactions. Analytics remains off when you choose
      “Essential only”. You can withdraw analytics consent by clearing FairPlay site data in
      your browser settings. We do not use Google Analytics for advertising tracking.
    </P>

    <H2>11. Children</H2>
    <Note>
      The Service is strictly for adults aged {COMPANY.minAge} and over. We do not knowingly
      collect information from anyone under {COMPANY.minAge}. If you believe a minor has provided us
      information, contact us and we will delete it.
    </Note>

    <H2>12. Changes to this Policy</H2>
    <P>
      We may update this Policy from time to time. If we make material changes we will take
      reasonable steps to notify you, and the "last updated" date above will change.
    </P>

    <H2>13. Contact</H2>
    <P>
      For any privacy question or request, email {COMPANY.privacyEmail}
      {COMPANY.address ? `, or write to ${COMPANY.address}, ${COMPANY.country}` : ''}.
    </P>
  </LegalLayout>
);

export default Privacy;
